import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { exportToExcel } from '../utils/excelExport';
import SortableHeader, { usePagedSortableData, TablePager } from './SortableTable';
import { exportExcelBtnCls } from './shared/ExportExcelButton';

// Adapted from the legacy component: the source data is now monthly aggregates
// ({userName, position, month, hours, isAudit}) so the expanded row shows the
// per-month audit / non-audit breakdown instead of per-task detail.
const ExpandableChargeableHours = ({ data, dateRange }) => {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [selectedPosition, setSelectedPosition] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  // Process and aggregate data by user
  const aggregatedData = useMemo(() => {
    const userMap = new Map();

    (data || []).forEach((record) => {
      const userName = record.userName || 'Unknown';
      const position = record.position || 'Unknown';
      const hours = Number(record.hours) || 0;
      const month = record.month || '';
      const isAudit = !!record.isAudit;

      if (!userMap.has(userName)) {
        userMap.set(userName, {
          userName,
          position,
          totalHours: 0,
          totalNonAudit: 0,
          totalAudit: 0,
          monthlyBreakdown: {},
        });
      }

      const user = userMap.get(userName);
      user.totalHours += hours;

      if (!user.monthlyBreakdown[month]) {
        user.monthlyBreakdown[month] = { audit: 0, nonAudit: 0 };
      }

      if (isAudit) {
        user.totalAudit += hours;
        user.monthlyBreakdown[month].audit += hours;
      } else {
        user.totalNonAudit += hours;
        user.monthlyBreakdown[month].nonAudit += hours;
      }
    });

    return Array.from(userMap.values()).sort((a, b) => b.totalHours - a.totalHours);
  }, [data]);

  // Filter by position
  const filteredData = useMemo(() => {
    let result = aggregatedData;

    // Filter by position
    if (selectedPosition !== 'All') {
      result = result.filter((user) => user.position === selectedPosition);
    }

    // Filter by search term
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter((user) =>
        user.userName.toLowerCase().includes(term) ||
        user.position.toLowerCase().includes(term)
      );
    }

    return result;
  }, [aggregatedData, selectedPosition, searchTerm]);

  // Sort the top-level user rows (default: total hours descending, matching the
  // original fixed ordering). Expanded monthly rows and the footer totals are
  // untouched by this.
  const { rows, sortConfig, requestSort, page, setPage, total } = usePagedSortableData(filteredData, {
    key: 'totalHours',
    direction: 'desc',
    accessor: (r) => r.totalHours,
  });

  // Get unique positions
  const positions = useMemo(() => {
    const posSet = new Set(aggregatedData.map((user) => user.position));
    return Array.from(posSet).sort();
  }, [aggregatedData]);

  // Toggle row expansion
  const toggleRow = (userName) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(userName)) {
      newExpanded.delete(userName);
    } else {
      newExpanded.add(userName);
    }
    setExpandedRows(newExpanded);
  };

  // Calculate totals
  const totals = useMemo(() => {
    return filteredData.reduce(
      (acc, user) => ({
        totalHours: acc.totalHours + user.totalHours,
        totalNonAudit: acc.totalNonAudit + user.totalNonAudit,
        totalAudit: acc.totalAudit + user.totalAudit,
      }),
      { totalHours: 0, totalNonAudit: 0, totalAudit: 0 }
    );
  }, [filteredData]);

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-blue-50 border border-blue-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-blue-600">Total Staff</p>
              <p className="text-2xl font-bold text-blue-900">{filteredData.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-green-50 border border-green-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-green-600">Total Hours</p>
              <p className="text-2xl font-bold text-green-900">
                {totals.totalHours.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-indigo-50 border border-indigo-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-indigo-600">Audit Hours</p>
              <p className="text-2xl font-bold text-indigo-900">
                {totals.totalAudit.toFixed(2)}
              </p>
              <p className="text-xs text-indigo-500">
                {totals.totalHours > 0 ? Math.round((totals.totalAudit / totals.totalHours) * 100) + '%' : '—'}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 p-4 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-orange-600">Non-audit Hours</p>
              <p className="text-2xl font-bold text-orange-900">
                {totals.totalNonAudit.toFixed(2)}
              </p>
              <p className="text-xs text-orange-500">
                {totals.totalHours > 0 ? Math.round((totals.totalNonAudit / totals.totalHours) * 100) + '%' : '—'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters: Position and Search */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div className="flex items-center space-x-4">
          <label className="text-sm font-medium text-gray-700">Filter by Designation:</label>
          <select
            value={selectedPosition}
            onChange={(e) => setSelectedPosition(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="All">All Designations ({aggregatedData.length})</option>
            {positions.map((position) => (
              <option key={position} value={position}>
                {position} ({aggregatedData.filter((u) => u.position === position).length})
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="text"
            placeholder="Search by name or position..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              Clear
            </button>
          )}
          <button
            onClick={() => {
              const rows = filteredData.map(user => ({
                'User Name': user.userName,
                'Designation': user.position,
                'Total Hours': parseFloat(user.totalHours.toFixed(2)),
                'Non-Audit Hours': parseFloat(user.totalNonAudit.toFixed(2)),
                'Audit Hours': parseFloat(user.totalAudit.toFixed(2)),
              }));
              exportToExcel(rows, 'Chargeable_Hours_Report.xlsx');
            }}
            className={exportExcelBtnCls}
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        </div>
      </div>

      {/* Expandable Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="w-full">
          <thead className="bg-blue-600 text-white">
            <tr>
              <SortableHeader label="User Name" sortKey="userName" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-3 font-semibold" />
              <SortableHeader label="Designation" sortKey="position" sortConfig={sortConfig} onSort={requestSort} className="px-4 py-3 font-semibold" />
              <SortableHeader label="Total Hours" sortKey="totalHours" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 font-semibold" />
              <SortableHeader label="Non-audit" sortKey="totalNonAudit" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 font-semibold" />
              <SortableHeader label="Audit" sortKey="totalAudit" sortConfig={sortConfig} onSort={requestSort} align="right" className="px-4 py-3 font-semibold" />
            </tr>
          </thead>
          <tbody>
            {filteredData.length === 0 ? (
              <tr>
                <td colSpan="5" className="px-4 py-8 text-center text-gray-500">
                  No data available for the selected filters
                </td>
              </tr>
            ) : (
              rows.map((user, idx) => (
                <React.Fragment key={user.userName}>
                  {/* Main Row */}
                  <tr
                    className={`border-b hover:bg-blue-50 transition-colors cursor-pointer ${
                      idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'
                    }`}
                    onClick={() => toggleRow(user.userName)}
                  >
                    <td className="px-4 py-3 font-medium text-gray-800">
                      <div className="flex items-center gap-2">
                        {expandedRows.has(user.userName) ? (
                          <ChevronDown size={18} className="text-blue-600" />
                        ) : (
                          <ChevronRight size={18} className="text-gray-400" />
                        )}
                        {user.userName}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-700">{user.position}</td>
                    <td className="px-4 py-3 text-right text-gray-700 font-semibold">
                      {user.totalHours.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-orange-600 font-semibold">
                      {user.totalNonAudit.toFixed(2)}
                    </td>
                    <td className="px-4 py-3 text-right text-blue-600 font-semibold">
                      {user.totalAudit.toFixed(2)}
                    </td>
                  </tr>

                  {/* Expanded Breakdown: Monthly detail */}
                  {expandedRows.has(user.userName) && (
                    <tr className="bg-blue-50">
                      <td colSpan="5" className="px-4 py-3">
                        <div className="ml-8 border-l-4 border-blue-400 pl-4">
                          <h4 className="font-semibold text-sm text-gray-700 mb-3">
                            Monthly Breakdown:
                          </h4>
                          {Object.keys(user.monthlyBreakdown).length === 0 ? (
                            <p className="text-sm text-gray-500 italic">No monthly records</p>
                          ) : (
                            <div className="space-y-2">
                              {Object.entries(user.monthlyBreakdown)
                                .sort((a, b) => a[0].localeCompare(b[0]))
                                .map(([month, monthData]) => (
                                  <div key={month} className="bg-white rounded shadow-sm">
                                    <div className="flex justify-between items-center px-3 py-2">
                                      <span className="text-sm font-medium text-gray-700">{month}</span>
                                      <div className="flex items-center gap-4 text-sm">
                                        <span className="text-blue-600">
                                          Audit: <span className="font-semibold">{monthData.audit.toFixed(2)} hrs</span>
                                        </span>
                                        <span className="text-orange-600">
                                          Non-audit: <span className="font-semibold">{monthData.nonAudit.toFixed(2)} hrs</span>
                                        </span>
                                        <span className="font-semibold text-gray-700">
                                          {(monthData.audit + monthData.nonAudit).toFixed(2)} hrs
                                        </span>
                                      </div>
                                    </div>
                                  </div>
                                ))}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))
            )}
          </tbody>

          {/* Footer Totals */}
          {filteredData.length > 0 && (
            <tfoot className="bg-gray-800 text-white font-bold">
              <tr>
                <td className="px-4 py-3" colSpan="2">
                  TOTAL
                </td>
                <td className="px-4 py-3 text-right">{totals.totalHours.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">{totals.totalNonAudit.toFixed(2)}</td>
                <td className="px-4 py-3 text-right">{totals.totalAudit.toFixed(2)}</td>
              </tr>
            </tfoot>
          )}
        </table>
        {/* Totals above stay whole-list totals; the pager only limits the rows. */}
        <TablePager
          page={page}
          setPage={setPage}
          total={total}
          className="flex items-center justify-between px-4 py-2 border-t border-gray-200 bg-gray-50"
        />
      </div>
    </div>
  );
};

export default ExpandableChargeableHours;
